import { useContext, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { format } from 'date-fns';
import { useQueryClient } from '@tanstack/react-query';

import {
  useAddProjectDataSource,
  useProjectDataSources,
  useRefreshProjectDataSource,
} from '../../api/projectDataSources';
import { useDataSources } from '../../api/dataSources';
import {
  ProjectAddableKind,
  ProjectDataSourceEntry,
  ProjectDataSourceKind,
} from '../../types/projectDataSource';
import { DataSource, SourceType } from '../../types/dataSource';
import { isRoadAssetSource } from '../createProject/types';
import { relativeTime } from '../../utils/datetime';
import { useAuth } from '../../contexts/AuthContext';
import { isViewOnly } from '../../utils/permissions';
import { StrategyContext } from '../../contexts/StrategyContext';

const KIND_LABELS: Record<ProjectDataSourceKind, string> = {
  roads: 'Stamped roads',
  vegetation: 'Vegetation',
  fire_history: 'Fire history',
  asset: 'Assets',
  treatment: 'Treatments',
  property_boundary: 'Property boundaries',
  hotspot: 'Hotspots',
  lightning: 'Lightning',
  hazard: 'Hazards incidents',
  ignition: 'Ignition points',
  burnt_area: 'Burnt areas',
};

/** What a refresh replaces, per kind — shown in the confirm dialog because
 *  the replacement is not undoable. */
const REFRESH_CONSEQUENCE: Record<ProjectDataSourceKind, string> = {
  roads:
    'Roads from this source are pulled again and cut out of the vegetation layer ' +
    'over the project area. Corridors already cut are unchanged; new or widened ' +
    'features are added.',
  vegetation:
    'The whole vegetation layer is re-pulled from the source and replaced. ' +
    'Any vegetation polygon edits made in this project are lost; per-class ' +
    'fuel settings (Vegetation Types) are kept. Fuel-load layers recompute afterwards.',
  fire_history:
    'Fire history records that came from this source are replaced with the ' +
    'latest data. Fire history you uploaded yourself is kept.',
  asset:
    'Assets that came from this source are replaced with the latest data ' +
    '(and get the project’s default construction type). Assets you added or ' +
    'edited by hand are kept, but edits to imported assets are lost.',
  treatment:
    'Treatments imported from this source are replaced with the latest data in ' +
    'every strategy they were imported into. Treatments you drew yourself are kept.',
  property_boundary:
    'Property boundaries that came from this source are replaced with the latest ' +
    'data over the project area. Properties from a file or another source are kept.',
  hotspot: '',
  lightning: '',
  hazard: '',
  ignition: '',
  burnt_area: '',
};

/** What can be added to an existing project, in the order offered, and
 *  which console source type is listed for each kind (roads sources carry
 *  the filters and line width that define the corridors). */
const ADD_KIND_OPTIONS: {
  kind: ProjectAddableKind;
  label: string;
  description: string;
  sourceType: SourceType;
}[] = [
  {
    kind: 'treatment',
    label: 'Treatments',
    description: 'Load treatments from a source into every strategy in this project.',
    sourceType: 'treatment',
  },
  {
    kind: 'asset',
    label: 'Assets',
    description: 'Import assets (buildings, infrastructure) from a source.',
    sourceType: 'asset',
  },
  {
    kind: 'fire_history',
    label: 'Fire history',
    description: 'Attach a fire history source and load its records over the project area.',
    sourceType: 'fire_history',
  },
  {
    kind: 'property_boundary',
    label: 'Property boundaries',
    description:
      'Load property (parcel) polygons from a source over the project area, alongside ' +
      'the boundaries already on the project.',
    sourceType: 'property_boundary',
  },
  {
    kind: 'roads',
    label: 'Stamp roads',
    description:
      'Cut road and track corridors from a roads source into the vegetation layer as ' +
      'non-burnable breaks. Lines use the width set on the source.',
    sourceType: 'roads',
  },
];

const ADD_DONE_MESSAGE: Record<ProjectAddableKind, string> = {
  treatment: 'Treatment source added to every strategy',
  asset: 'Asset source added',
  fire_history: 'Fire history source added',
  property_boundary: 'Property boundaries added',
  roads: 'Roads stamped into the vegetation layer',
};

interface Props {
  projectId: string | undefined;
  /** Live project status: refreshes are only allowed while "ready". */
  projectStatus: string | undefined;
  onNotify: (message: string, severity: 'success' | 'error') => void;
}

function LastRefreshed({ entry }: { entry: ProjectDataSourceEntry }) {
  if (entry.status === 'refreshing') {
    const progress =
      entry.tiles_total > 1
        ? ` ${entry.tiles_completed} of ${entry.tiles_total} tiles`
        : '';
    return (
      <Typography
        variant="body2"
        sx={{ color: '#00d4ad', display: 'flex', alignItems: 'center', gap: 0.75 }}
      >
        <CircularProgress size={12} sx={{ color: '#00d4ad' }} />
        Refreshing…{progress}
      </Typography>
    );
  }
  const absolute = entry.last_refreshed_at
    ? format(new Date(entry.last_refreshed_at), 'MMM d, yyyy h:mma')
    : null;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minWidth: 0 }}>
      {entry.status === 'failed' && (
        <Tooltip title={entry.last_error ?? 'The last refresh failed'}>
          <ErrorOutlineIcon sx={{ fontSize: 16, color: '#ef4444' }} />
        </Tooltip>
      )}
      {entry.status === 'idle' && entry.last_error && (
        <Tooltip title={entry.last_error}>
          <WarningAmberIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
        </Tooltip>
      )}
      <Tooltip title={absolute ?? 'Loaded when the project was created; not refreshed since'}>
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.6)', whiteSpace: 'nowrap' }}>
          {entry.status === 'failed' ? 'Refresh failed' : entry.last_refreshed_at ? relativeTime(entry.last_refreshed_at) : 'Not refreshed'}
          {entry.status === 'failed' && entry.last_refreshed_at ? ` · last ok ${relativeTime(entry.last_refreshed_at)}` : ''}
        </Typography>
      </Tooltip>
    </Box>
  );
}

export default function ProjectDataSourcesSection({ projectId, projectStatus, onNotify }: Props) {
  const { user } = useAuth();
  const viewOnly = isViewOnly(user);
  const queryClient = useQueryClient();
  const { refreshStrategy } = useContext(StrategyContext);
  const { data, isLoading, error } = useProjectDataSources(projectId);
  const refresh = useRefreshProjectDataSource(projectId);
  const addSource = useAddProjectDataSource(projectId);
  const [confirmEntry, setConfirmEntry] = useState<ProjectDataSourceEntry | null>(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addKind, setAddKind] = useState<ProjectAddableKind | null>(null);
  const [addSourceId, setAddSourceId] = useState<number | null>(null);
  // Which action started the in-flight load, for the completion toast.
  const lastActionRef = useRef<'refresh' | ProjectAddableKind>('refresh');

  // The tenant's console sources, per kind the picker can offer.
  // One unfiltered request, split client-side: five parallel per-kind calls
  // to /api/data_sources were flagged as an N+1 (fisci-front-end #402).
  const allSourcesQ = useDataSources();
  const sourcesFor = (kind: ProjectAddableKind): { sources: DataSource[]; loading: boolean } => {
    const ofKind = (t: SourceType) => allSourcesQ.dataSources.filter((s) => s.source_type === t);
    const loading = allSourcesQ.isLoading;
    switch (kind) {
      case 'asset':
        // The built-in Roads layer is stamped at init, never imported as assets.
        return { sources: ofKind('asset').filter((s) => !isRoadAssetSource(s)), loading };
      case 'fire_history':
        return { sources: ofKind('fire_history'), loading };
      case 'property_boundary':
        return { sources: ofKind('property_boundary'), loading };
      case 'roads':
        return { sources: ofKind('roads'), loading };
      default:
        return { sources: ofKind('treatment'), loading };
    }
  };

  const entries = data?.data ?? [];
  const anyRefreshing = entries.some((e) => e.status === 'refreshing');
  const projectBusy = projectStatus !== undefined && projectStatus !== 'ready';
  const attachedIds = (kind: ProjectDataSourceKind) =>
    new Set(entries.filter((e) => e.kind === kind).map((e) => e.source.id));
  const existingFireHistory = entries.find((e) => e.kind === 'fire_history');
  /** Why a kind cannot be added right now, or null. */
  const addKindBlocked = (kind: ProjectAddableKind): string | null =>
    kind === 'fire_history' && existingFireHistory
      ? `This project already has a fire history source (${existingFireHistory.source.name}); refresh it instead.`
      : null;
  const pickable = addKind ? sourcesFor(addKind) : { sources: [], loading: false };
  const availableSources = addKind
    ? pickable.sources.filter((s) => !attachedIds(addKind).has(s.id))
    : [];

  // When a refresh finishes, the project's layers are stale: bump the
  // vegetation tile version and drop cached derived data so the map reloads.
  const wasRefreshingRef = useRef(false);
  useEffect(() => {
    if (wasRefreshingRef.current && !anyRefreshing && projectId) {
      queryClient.setQueryData(['vegetation-tile-version', projectId], () => Date.now());
      queryClient.invalidateQueries({ queryKey: ['project', projectId] });
      queryClient.invalidateQueries({ queryKey: ['vegetationSources'] });
      queryClient.invalidateQueries({ queryKey: [projectId, 'assets'] });
      queryClient.invalidateQueries({ queryKey: [projectId, 'asset-counts'] });
      queryClient.invalidateQueries({ queryKey: [projectId, 'asset-tiles-url'] });
      queryClient.invalidateQueries({ queryKey: [projectId, 'fireHistory'] });
      queryClient.invalidateQueries({ queryKey: ['strategy'] });
      queryClient.invalidateQueries({ queryKey: ['strategyJob'] });
      queryClient.invalidateQueries({ queryKey: ['strategyStatus'] });
      refreshStrategy();
      const failed = entries.find((e) => e.status === 'failed');
      const action = lastActionRef.current;
      if (failed) {
        onNotify(
          `${action === 'refresh' ? 'Refresh' : 'Loading'} of ${failed.source.name} failed: ${failed.last_error ?? 'unknown error'}`,
          'error'
        );
      } else {
        onNotify(action === 'refresh' ? 'Data refresh complete' : ADD_DONE_MESSAGE[action], 'success');
      }
      lastActionRef.current = 'refresh';
    }
    wasRefreshingRef.current = anyRefreshing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyRefreshing, projectId]);

  const handleConfirm = async () => {
    if (!confirmEntry) return;
    const entry = confirmEntry;
    setConfirmEntry(null);
    try {
      lastActionRef.current = 'refresh';
      await refresh.mutateAsync({ sourceId: entry.source.id, kind: entry.kind });
      onNotify(`Refreshing ${entry.source.name}…`, 'success');
    } catch (err) {
      const message = (err as { message?: string } | null)?.message;
      onNotify(message || 'Failed to start refresh', 'error');
    }
  };

  const closeAdd = () => {
    setAddOpen(false);
    setAddKind(null);
    setAddSourceId(null);
  };

  const ADD_STARTED_MESSAGE: Record<ProjectAddableKind, (name: string) => string> = {
    treatment: (n) => `Adding ${n}: loading treatments into every strategy…`,
    asset: (n) => `Adding ${n}: importing assets…`,
    fire_history: (n) => `Adding ${n}: loading fire history…`,
    property_boundary: (n) => `Adding ${n}: loading property boundaries…`,
    roads: (n) => `Stamping roads from ${n} into the vegetation layer…`,
  };

  const handleAdd = async () => {
    if (addKind === null || addSourceId === null) return;
    const kind = addKind;
    const source = availableSources.find((s) => s.id === addSourceId);
    closeAdd();
    try {
      lastActionRef.current = kind;
      await addSource.mutateAsync({ kind, sourceId: addSourceId });
      onNotify(ADD_STARTED_MESSAGE[kind](source?.name ?? 'data source'), 'success');
    } catch (err) {
      lastActionRef.current = 'refresh';
      const message = (err as { message?: string } | null)?.message;
      onNotify(message || 'Failed to add data source', 'error');
    }
  };

  const addDisabled = viewOnly || anyRefreshing || projectBusy || addSource.isPending;
  const addDisabledReason = viewOnly
    ? 'View-only users cannot add data sources'
    : anyRefreshing
      ? 'Wait for the current refresh to finish'
      : projectBusy
        ? `Project is ${projectStatus}`
        : '';

  return (
    <>
      <Typography variant="h6" sx={{ mb: 0.5, fontWeight: 500, color: '#fff' }}>
        Data Sources
      </Typography>
      <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 2 }}>
        The sources this project was built from. Refresh one to re-pull its latest
        data over the project area; the project stays usable while it runs.
      </Typography>

      {isLoading ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, color: 'rgba(255,255,255,0.6)' }}>
          <CircularProgress size={16} sx={{ color: '#00d4ad' }} />
          <Typography variant="body2">Loading data sources…</Typography>
        </Box>
      ) : error ? (
        <Typography variant="body2" sx={{ color: '#ef4444', mb: 1.5 }}>
          Could not load data sources.
        </Typography>
      ) : entries.length === 0 ? (
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)', mb: 1.5 }}>
          This project has no data sources attached.
        </Typography>
      ) : (
        <Box
          sx={{
            border: '1px solid rgba(255, 255, 255, 0.1)',
            borderRadius: 1,
            mb: 1.5,
            overflow: 'hidden',
          }}
        >
          {entries.map((entry, i) => {
            const refreshing = entry.status === 'refreshing';
            const disabled = viewOnly || refreshing || anyRefreshing || projectBusy || refresh.isPending;
            const disabledReason = viewOnly
              ? 'View-only users cannot refresh data'
              : refreshing
                ? 'Refresh in progress'
                : anyRefreshing
                  ? 'Wait for the current refresh to finish'
                  : projectBusy
                    ? `Project is ${projectStatus}`
                    : '';
            return (
              <Box
                key={`${entry.kind}-${entry.source.id}`}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  px: 2,
                  py: 1.25,
                  borderTop: i === 0 ? 'none' : '1px solid rgba(255, 255, 255, 0.08)',
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: '#00d4ad', textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}
                  >
                    {KIND_LABELS[entry.kind] ?? entry.kind}
                  </Typography>
                  <Typography
                    variant="body2"
                    sx={{ color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    title={entry.source.name}
                  >
                    {entry.source.name}
                  </Typography>
                </Box>
                <Box sx={{ minWidth: 150, display: 'flex', justifyContent: 'flex-end' }}>
                  <LastRefreshed entry={entry} />
                </Box>
                <Tooltip title={disabledReason}>
                  <span>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<RefreshIcon />}
                      disabled={disabled}
                      onClick={() => setConfirmEntry(entry)}
                      sx={{
                        color: '#00d4ad',
                        borderColor: 'rgba(0, 212, 173, 0.5)',
                        whiteSpace: 'nowrap',
                        '&:hover': { borderColor: '#00d4ad', backgroundColor: 'rgba(0, 212, 173, 0.08)' },
                        '&.Mui-disabled': {
                          color: 'rgba(255, 255, 255, 0.3)',
                          borderColor: 'rgba(255, 255, 255, 0.1)',
                        },
                      }}
                    >
                      Refresh
                    </Button>
                  </span>
                </Tooltip>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Sources that can be attached after creation: treatments, assets,
          fire history and property boundaries append their own rows; "stamp
          roads" cuts corridors from a source into the vegetation layer.
          Vegetation itself is one surface per project and is only ever
          refreshed. */}
      <Box sx={{ mb: 3 }}>
        <Tooltip title={addDisabledReason}>
          <span>
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              disabled={addDisabled}
              onClick={() => setAddOpen(true)}
              sx={{
                color: '#00d4ad',
                borderColor: 'rgba(0, 212, 173, 0.5)',
                whiteSpace: 'nowrap',
                '&:hover': { borderColor: '#00d4ad', backgroundColor: 'rgba(0, 212, 173, 0.08)' },
                '&.Mui-disabled': {
                  color: 'rgba(255, 255, 255, 0.3)',
                  borderColor: 'rgba(255, 255, 255, 0.1)',
                },
              }}
            >
              Add data source
            </Button>
          </span>
        </Tooltip>
      </Box>

      <Dialog
        open={addOpen}
        onClose={closeAdd}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { backgroundColor: '#1e1e1e', color: '#fff' } }}
      >
        <DialogTitle>Add data source</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)', mb: 1.5 }}>
            What do you want to add?
          </DialogContentText>
          <RadioGroup
            value={addKind ?? ''}
            onChange={(e) => {
              setAddKind(e.target.value as ProjectAddableKind);
              setAddSourceId(null);
            }}
          >
            {ADD_KIND_OPTIONS.map((opt) => {
              const blocked = addKindBlocked(opt.kind);
              return (
                <Tooltip key={opt.kind} title={blocked ?? ''} placement="right">
                  <FormControlLabel
                    value={opt.kind}
                    disabled={blocked !== null}
                    control={<Radio sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#00d4ad' } }} />}
                    sx={{ alignItems: 'flex-start', mb: 0.5, mr: 0 }}
                    label={
                      <Box sx={{ pt: 1 }}>
                        <Typography variant="body2" sx={{ color: blocked ? 'rgba(255,255,255,0.4)' : '#fff', fontWeight: 500 }}>
                          {opt.label}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                          {blocked ?? opt.description}
                        </Typography>
                      </Box>
                    }
                  />
                </Tooltip>
              );
            })}
          </RadioGroup>

          {addKind && (
            <>
              <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)', mt: 2, mb: 1 }}>
                {addKind === 'roads'
                  ? 'Choose the roads source to cut out of the vegetation. Its filters and line width are set under Roads in the Data Management console.'
                  : addKind === 'property_boundary'
                    ? 'Choose a property boundary source. Its attribute filters and name field are set under Property Boundaries in the Data Management console.'
                    : 'Choose a source.'}
              </DialogContentText>
              {pickable.loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'rgba(255,255,255,0.6)' }}>
                  <CircularProgress size={16} sx={{ color: '#00d4ad' }} />
                  <Typography variant="body2">Loading sources…</Typography>
                </Box>
              ) : availableSources.length === 0 ? (
                <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                  {pickable.sources.length === 0
                    ? 'No matching data sources are configured. Add one in the Data Management console first.'
                    : 'Every matching source is already attached to this project.'}
                </Typography>
              ) : (
                <RadioGroup
                  value={addSourceId ?? ''}
                  onChange={(e) => setAddSourceId(Number(e.target.value))}
                >
                  {availableSources.map((source) => (
                    <FormControlLabel
                      key={source.id}
                      value={source.id}
                      control={<Radio sx={{ color: 'rgba(255,255,255,0.5)', '&.Mui-checked': { color: '#00d4ad' } }} />}
                      sx={{ alignItems: 'flex-start', mb: 0.5, mr: 0 }}
                      label={
                        <Box sx={{ pt: 1 }}>
                          <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                            {source.name}
                          </Typography>
                          {typeof source.config?.default_type === 'string' && addKind === 'treatment' && (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                              Default type: {source.config.default_type}
                            </Typography>
                          )}
                          {addKind === 'property_boundary' &&
                            Array.isArray(source.config?.filters) &&
                            (source.config.filters as unknown[]).length > 0 && (
                              <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                                {(source.config.filters as unknown[]).length} attribute filter(s)
                              </Typography>
                            )}
                          {addKind === 'roads' && (
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                              {source.adapter_type === 'mapbox_vector_tiles'
                                ? 'Widths by road class (Mapbox Streets)'
                                : `Line width: ${typeof source.config?.line_width_m === 'number' ? `${source.config.line_width_m} m` : 'default 10 m'}`}
                              {Array.isArray(source.config?.filters) && (source.config.filters as unknown[]).length > 0
                                ? ` · ${(source.config.filters as unknown[]).length} filter(s)`
                                : ''}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  ))}
                </RadioGroup>
              )}
            </>
          )}
          {addKind && addSourceId !== null && (
            <DialogContentText sx={{ color: '#f59e0b', mt: 2 }}>
              {addKind === 'roads'
                ? 'Vegetation under the corridors is removed. This cannot be undone; existing simulations keep their results but will be reported as out of date.'
                : 'Existing data in the project is kept. You can refresh the source later from this list.'}
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAdd} sx={{ color: '#fff' }}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            startIcon={<AddIcon />}
            disabled={addKind === null || addSourceId === null}
            sx={{
              backgroundColor: '#00d4ad',
              color: '#000',
              '&:hover': { backgroundColor: '#00b894' },
              '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.3)' },
            }}
          >
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmEntry !== null}
        onClose={() => setConfirmEntry(null)}
        PaperProps={{ sx: { backgroundColor: '#1e1e1e', color: '#fff' } }}
      >
        <DialogTitle>
          Refresh {confirmEntry ? KIND_LABELS[confirmEntry.kind].toLowerCase() : 'data'} from{' '}
          {confirmEntry?.source.name}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ color: 'rgba(255,255,255,0.7)' }}>
            {confirmEntry ? REFRESH_CONSEQUENCE[confirmEntry.kind] : ''}
          </DialogContentText>
          <DialogContentText sx={{ color: '#f59e0b', mt: 2 }}>
            This cannot be undone. Existing simulations keep their results but
            will be reported as out of date.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmEntry(null)} sx={{ color: '#fff' }}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            startIcon={<RefreshIcon />}
            sx={{ backgroundColor: '#00d4ad', color: '#000', '&:hover': { backgroundColor: '#00b894' } }}
          >
            Refresh
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
